/**
 * What the same accounts are worth on a different valuation argument.
 *
 * Accounting's what-if scales revenue, headcount and opex, and overrides the COGS,
 * interest and tax rates. Every one of those is a question about the STATEMENTS. None of
 * them is what a valuation is argued about, which is why this exists rather than the
 * valuation discipline borrowing that view: a disagreement about what a company is worth
 * is a disagreement about the cost of capital, the growth that lasts for ever, the
 * multiple a buyer would pay, and how much of the reported EBITDA is real.
 *
 * COMPUTED ON THE BACKEND, always. `calculate_valuation_what_if` calls the same
 * `calculate_valuation_v2` the model itself uses, with a copy of the settings and a copy
 * of the flows. A discounted cash flow implemented here to save a round trip would drift
 * from the one on the server, and nothing on the screen would say which the user was
 * looking at.
 *
 * THE CHANGE IS THE POINT, so it is what the eye lands on. A scenario's equity value
 * means nothing without the figure it moved from - two columns of large numbers compared
 * by eye is how a 3% difference gets called material.
 */

import { useEffect, useState } from 'react';
import { WorkingIndicator } from '../../../components/WorkingIndicator';
import { formatMoney } from '../../../lib/currency';
import { useFinancialModelV2Store } from '../../../store/financial-model-v2';
import { useAuthStore } from '../../../store/auth';

interface ScenarioOutcome {
  name: string;
  equityValue: number | null;
  perShare: number | null;
  enterpriseValue: number | null;
  changeVsBase: number | null;
  changePercent: number | null;
  moved: string[];
  notes: string[];
}

interface WhatIfResult {
  base: ScenarioOutcome;
  scenarios: ScenarioOutcome[];
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function money(value: number | null, currency: string): string {
  if (value === null) return '—';
  return formatMoney(value, currency);
}

/**
 * A movement, coloured by direction.
 *
 * Red for down and green for up, and NOT the other way round even though a lower
 * valuation is good news for a buyer. The convention a reader brings is that red is a
 * fall, and a view that inverted it to suit one side of a negotiation would be read
 * wrongly by everyone on the other.
 */
function Change({
  outcome,
  currency,
}: {
  outcome: ScenarioOutcome;
  currency: string;
}) {
  if (outcome.changeVsBase === null) return <span className="text-gray-400">—</span>;
  const down = outcome.changeVsBase < 0;
  return (
    <span className={down ? 'text-red-700' : 'text-green-700'}>
      {down ? '' : '+'}
      {formatMoney(outcome.changeVsBase, currency)}
      {outcome.changePercent !== null && (
        <span className="ml-1.5 text-xs">
          ({outcome.changePercent >= 0 ? '+' : ''}
          {(outcome.changePercent * 100).toFixed(1)}%)
        </span>
      )}
    </span>
  );
}

export function ValuationWhatIfV2() {
  const model = useFinancialModelV2Store((s) => s.model);
  const [result, setResult] = useState<WhatIfResult | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const modelId = model?.id;

  useEffect(() => {
    if (!modelId) return;
    let cancelled = false;

    const run = async () => {
      setIsRunning(true);
      setError(null);
      try {
        const response = await fetch(
          `/api/v2/models/${modelId}/valuation-what-if`,
          { method: 'POST', headers: authHeaders() },
        );
        if (!response.ok) {
          const detail = await response.json().catch(() => ({}));
          throw new Error(
            detail.detail ?? `The scenarios could not be valued (${response.status})`,
          );
        }
        const body: WhatIfResult = await response.json();
        // The request outlived the tab. Writing state now would show one model's
        // scenarios under another's name.
        if (!cancelled) setResult(body);
      } catch (caught) {
        if (!cancelled) {
          setError(caught instanceof Error ? caught.message : String(caught));
        }
      } finally {
        if (!cancelled) setIsRunning(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
    // Re-run when the ASSUMPTIONS move, not only when the model changes: the whole
    // output is relative to a base case computed from them, so a discount rate edited
    // on Settings makes every row on this screen stale.
  }, [modelId, model?.version]);

  if (!model) return null;
  const currency = model.settings.baseCurrency;

  return (
    <div className="h-full overflow-auto p-6">
      <h2 className="mb-1 text-lg font-bold text-gray-800">What-if</h2>
      <p className="mb-4 max-w-3xl text-sm text-gray-500">
        What these accounts are worth on a different argument. Nothing here changes the
        accounts — the statements stay exactly as they are, and only what the valuation
        makes of them moves.
      </p>

      {isRunning && (
        <div className="mb-4 max-w-3xl rounded border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          <WorkingIndicator label="Valuing each scenario" />
        </div>
      )}

      {error && (
        <div className="mb-4 max-w-3xl rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {result && (
        <div className="max-w-5xl overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="border-b border-gray-200 px-3 py-2 font-medium">
                  Scenario
                </th>
                <th className="border-b border-gray-200 px-3 py-2 text-right font-medium">
                  Equity value
                </th>
                <th className="border-b border-gray-200 px-3 py-2 text-right font-medium">
                  Per share
                </th>
                <th className="border-b border-gray-200 px-3 py-2 text-right font-medium">
                  Against base
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-gray-50">
                <td className="border-b border-gray-200 px-3 py-2 font-semibold text-gray-900">
                  {result.base.name}
                  <div className="mt-0.5 text-xs font-normal text-gray-500">
                    The model's own assumptions, as Settings has them.
                  </div>
                </td>
                <td className="border-b border-gray-200 px-3 py-2 text-right font-semibold tabular-nums text-gray-900">
                  {money(result.base.equityValue, currency)}
                </td>
                <td className="border-b border-gray-200 px-3 py-2 text-right tabular-nums text-gray-700">
                  {money(result.base.perShare, currency)}
                </td>
                <td className="border-b border-gray-200 px-3 py-2 text-right text-gray-400">
                  —
                </td>
              </tr>

              {result.scenarios.map((scenario) => (
                <tr key={scenario.name}>
                  <td className="border-b border-gray-100 px-3 py-2 text-gray-800">
                    {scenario.name}
                    {scenario.moved.length > 0 && (
                      <div className="mt-0.5 text-xs text-gray-500">
                        {scenario.moved.join(' · ')}
                      </div>
                    )}
                  </td>
                  <td className="border-b border-gray-100 px-3 py-2 text-right tabular-nums text-gray-900">
                    {money(scenario.equityValue, currency)}
                  </td>
                  <td className="border-b border-gray-100 px-3 py-2 text-right tabular-nums text-gray-700">
                    {money(scenario.perShare, currency)}
                  </td>
                  <td className="border-b border-gray-100 px-3 py-2 text-right tabular-nums">
                    <Change outcome={scenario} currency={currency} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Which lever matters most is the answer somebody came for, and it is not
              always the one they expected. Stated rather than left to be read off the
              column. */}
          {(() => {
            const largest = [...result.scenarios]
              .filter((s) => s.changeVsBase !== null)
              .sort(
                (a, b) => Math.abs(b.changeVsBase!) - Math.abs(a.changeVsBase!),
              )[0];
            if (!largest) return null;
            return (
              <p className="mt-4 max-w-3xl text-sm text-gray-600">
                <span className="font-medium text-gray-800">
                  {largest.name}
                </span>{' '}
                moves the value most, by {money(largest.changeVsBase, currency)}. That
                is where an argument about this valuation is worth having.
              </p>
            );
          })()}

          {result.base.notes.length > 0 && (
            <div className="mt-4 max-w-3xl rounded border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
              <div className="mb-1 font-medium">
                About the base case, and so about every row
              </div>
              <ul className="list-disc space-y-1 pl-4">
                {result.base.notes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
